/**
 * 1.8.4 §3 (D-1.8.4-3) — Today: the whole house at a glance. The shape of `GET /api/house-today?day=yyyyMMdd` and the
 * pure rules the API and the page share.
 *
 * The page is a glance: nothing on it books, moves, cancels or sends. Every line points to the program page where it
 * is changed ("Open in Housekeeping"). Names follow the house calendar (shared/house-calendar.ts nameLevelOf): the
 * administrator and a co-administrator see the names they already see on the program pages; the viewer sees titles
 * and counts only — the API never sends a name the role does not already see.
 *
 * Five parts, as the page draws them: the greeting (client-side, from "Call me" in My settings), Needs your attention
 * (one line per kind across every program), the day (the month calendar's lines for that day, plus each housekeeping
 * visit for the office roles), the emails armed ahead of time for that day (event reminders, D-1.7.7-0) and the recent
 * reports (the last two days' close-outs with notes, incidents, unit-condition reports and home visit records — one
 * line each, never the narrative).
 *
 * No dependencies beyond the shared rules, so the app and the API share it.
 */
import type { NameLevel } from './house-calendar';
import type { VisitKind } from './api-types';
import { cleanCallMe } from './my-settings';
import type { CompanionItem } from './volunteers-api';

/**
 * The programs a line belongs to (the top row's words), Tech Support and Events — and, last, Volunteers (19 Oct 2026,
 * D-1019-ATTN): an attention group only (a new volunteer to review serves every program), never a column of the day.
 */
export const HOUSE_PROGRAMS = ['housekeeping', 'companion', 'reading', 'tech', 'events', 'volunteers'] as const;
export type HouseProgram = (typeof HOUSE_PROGRAMS)[number];
/** The programs the day's lines belong to: the grid's columns and the month calendar's legend (no Volunteers). */
export const HOUSE_DAY_PROGRAMS: readonly HouseProgram[] = HOUSE_PROGRAMS.filter((p) => p !== 'volunteers');

/** The program strip's tab a program opens in ("Open in Housekeeping"); null: no tab (Tech Support, Events, Volunteers). */
export function stripProgramOf(p: HouseProgram | null | undefined): 'housekeeping' | 'companion' | 'reading' | null {
  return p === 'housekeeping' || p === 'companion' || p === 'reading' ? p : null;
}

/* ------------------------------------------------------------------------------------------------
 * Needs your attention — one line per kind
 * ---------------------------------------------------------------------------------------------- */

/**
 * Each kind is a list a program page already makes (nothing here decides anything new):
 *   reachOut            visits today and tomorrow with no confirmation (Today's "unconfirmed for tomorrow", and the
 *                       same rule for today)
 *   pendingCloseout     visits not closed out the reminder's time after they ended (crew, tech, volunteer)
 *   hold                visits on hold
 *   needsNewDay         the "Needs a new day" list (a Tech Support ticket the tech found nobody home for is one, under Tech Support)
 *   techNew             1.8.5 (D-1.8.5-3): Tech Support tickets their tech has not opened yet
 *   incident            Housekeeping: close-outs that wait for the administrator's review (a health or safety
 *                       concern); Companion: incident reports awaiting review and their follow-ups
 *   more                everything else on Housekeeping's own Needs your attention (proposals, answers, replies…)
 *   outingTomorrow … activityHelp   the Companion tab's items, by kind
 *   untaken             volunteer requests nobody has taken yet (a one-time request more than COMPANION_NO_VOLUNTEER_DAYS days out)
 *   noVolunteer         22 Oct 2026 (D-1022-COMPANION): a one-time Companion or Reading request still open 2 days out or less
 *   shortVolunteers     upcoming events with fewer volunteers than they need
 *   newVolunteer        19 Oct 2026 (D-1019-ATTN): sign-ups from the QR code waiting for the office's Approve — under
 *                       Volunteers, not Companion & Wellness
 *   readingCloseout     23 Oct 2026 (D-1023-READING): a Reading & Services visit waiting for the office's close-out — Reading's alone
 *   activityCloseout    23 Oct 2026 (D-1023-ROUTING): an event or activity helper's visit waiting for the office's close-out — Events'
 *   readingNote         23 Oct 2026 (D-1023-READING): a Reading volunteer's note after the thank-you email — Reading's alone

 *   complaintFollowUp   25 Oct 2026 (D-1025-TODAY): a tenant's complaint (SM_TenantFeedback, IsComplaint) still New or Acknowledged — the office's
 *                       to follow up; Housekeeping's (the feedback form is Housekeeping's), the office roles only
 *   agreementOpened     1 Nov 2026 (D-1101-SMALL-2): a tenant opened their agreement link more than 24 hours ago and has not signed —
 *                       one line per person, under the program the agreement belongs to; the administrator and a co-administrator only
 *   outingNotSent       25 Oct 2026 (D-1025-TODAY): an upcoming group outing whose own file says nobody was sent the invitation — Companion's
 *   inbox               23 Oct 2026 (D-1023-INBOX): replies and bounced emails waiting in Messages → Inbox — ONE line, last (staff; the scheduler only those tied to a Housekeeping visit)
 *   emailFailed         4 Nov 2026 (D-1104-MBX-D-3): emails that could not be sent (Messages → Failed) — ONE line under Messages, the administrator and
 *                       co-administrator only, behind Settings → EMAIL_FAILED_ON_TODAY (default Yes); up to three named (recipient · subject)
 *   secondVisit         4 Nov 2026 (D-1104-NEEDS Q7): from the 15th, tenants with only 1 of 2 visits this month - ONE line under Housekeeping, the
 *                       administrator and co-administrator (the scheduler's is on the Housekeeping Today), up to three named; opens Planning at Still needs a date
 *   volunteerNotice     22 Oct 2026 (D-1022-ONBOARD): a volunteer finished registering or updated their information from
 *                       their own link and the office has not pressed "Got it" — under Volunteers, last
 */
export const HOUSE_ATTN_KINDS = [
  'reachOut',
  'pendingCloseout',
  'hold',
  'needsNewDay',
  'techNew',
  'incident',
  'more',
  'complaintFollowUp',
  'agreementOpened',
  'outingTomorrow',
  'outingNotSent',
  'volunteerUnconfirmed',
  'needsOk',
  'claimed',
  'answers',
  'completionsPending',
  'visionsReady',
  'visionsReferral',
  'activityHelp',
  'workshopProposal',
  'untaken',
  'noVolunteer',
  'shortVolunteers',
  'newVolunteer',
  'volunteerNotice',
  'readingCloseout',
  'readingNote',
  'activityCloseout',
  'inbox',
  'emailFailed',
  'secondVisit',
] as const;
/** The Admin → Settings switch for the failed-email line on Today (D-1104-MBX-D-3). Default Yes. */
export const EMAIL_FAILED_ON_TODAY_KEY = 'EMAIL_FAILED_ON_TODAY';
export type HouseAttnKind = (typeof HOUSE_ATTN_KINDS)[number];

/**
 * 25 Oct 2026 (D-1025-TODAY): one NAMED thing that needs the office — "Apt 4C · Thursday visit not confirmed" — with the
 * exact address of the place it is fixed. The words are written by the page in the reader's language from these values
 * (nothing here is a sentence), so the same read serves English and Spanish. Only the office roles get items: for the
 * viewer `items` is empty and the line is the count it always was.
 */
export interface HouseAttnItem {
  id: string;
  /** "4C · Name" for a visit or a tenant; null where the item names no person. */
  who: string | null;
  /** The day the item is about (yyyyMMdd): the visit, the request, the event, the complaint's visit. */
  dayKey: string | null;
  /** A hold: when it ends (ISO instant). */
  until: string | null;
  /** The kind of thing on Housekeeping's or Tech Support's own list this stands for ("proposal", "notHome"…), for the sentence; null otherwise. */
  what: string | null;
  /** The event's, outing's or volunteer's own name, when the item is about one. */
  label: string | null;
  /** An event short of volunteers: those holding a place, and the number needed. */
  have: number | null;
  need: number | null;
  /** A request nobody has taken: what it is for, so the page can say it in the reader's language. */
  activity: { category: string; trip: string | null; otherWhat: string | null } | null;
  /** The Companion tab's own item, when the line comes from it (the page writes it with `companionItemWords`; no narrative). */
  companion: CompanionItem | null;
  /** The exact address of the thing, or null when the role cannot open it. */
  href: string | null;
  openIn: 'housekeeping' | 'companion' | 'reading' | null;
  /** yyyyMMdd the item is due or happens, for the sort ("sorted by deadline"); null: no date. */
  dueKey: string | null;
}

export interface HouseAttnLine {
  /** `<kind>-<program>` — unique on the page. */
  id: string;
  kind: HouseAttnKind;
  /** The program the line is about (its word on the screen). */
  program: HouseProgram;
  count: number;
  /** Where it is fixed (an address inside the app), or null when the role cannot open it. */
  href: string | null;
  /** The program strip's tab that address opens in (null: no tab — Events, or a page outside the strip). */
  openIn: 'housekeeping' | 'companion' | 'reading' | null;
  /** 25 Oct 2026: the named things the count stands for (office roles). Empty: the line is the count alone. */
  items: HouseAttnItem[];
}

/** Where a program's heading on Today opens, and the strip tab it moves to (null: no tab). */
export const PROGRAM_HOME: Record<HouseProgram, { href: string; openIn: 'housekeeping' | 'companion' | 'reading' | null }> = {
  housekeeping: { href: '/housekeeping', openIn: 'housekeeping' },
  companion: { href: '/companion', openIn: 'companion' },
  reading: { href: '/reading', openIn: 'reading' },
  tech: { href: '/tech', openIn: null },
  events: { href: '/events', openIn: null },
  volunteers: { href: '/volunteers', openIn: null },
};

/** How many named lines a program group shows before "and N more". */
export const ATTN_GROUP_CAP = 3;

/** A group's entries as the page draws them: every item, or — for a line with none — the line itself as one entry. */
export type HouseAttnEntry = { key: string; line: HouseAttnLine; item: HouseAttnItem | null; dueKey: string | null };

/** One program's group: its entries by deadline (dated first), the first CAP shown and the rest counted. */
export function groupEntries(lines: readonly HouseAttnLine[], program: HouseProgram, cap: number = ATTN_GROUP_CAP): { count: number; entries: HouseAttnEntry[]; shown: HouseAttnEntry[]; more: number } {
  const mine = lines.filter((l) => l.program === program);
  const entries = mine
    .flatMap((l) => (l.items.length ? l.items.map((item): HouseAttnEntry => ({ key: item.id, line: l, item, dueKey: item.dueKey })) : [{ key: l.id, line: l, item: null, dueKey: null } as HouseAttnEntry]))
    .map((e, n) => ({ e, n }))
    .sort((a, b) => (a.e.dueKey ?? '99999999').localeCompare(b.e.dueKey ?? '99999999') || a.n - b.n)
    .map((x) => x.e);
  const shown = entries.slice(0, cap);
  return { count: mine.reduce((n, l) => n + l.count, 0), entries, shown, more: entries.length - shown.length };
}

/* ------------------------------------------------------------------------------------------------
 * The day: each housekeeping visit (office roles only)
 * ---------------------------------------------------------------------------------------------- */

export interface HouseVisit {
  appointmentId: string;
  unit: string;
  tenantName: string;
  /** The number Call dials — never printed on the page (D-1.8.3-1). */
  phone: string | null;
  position: number | null;
  /** Minutes after midnight, New York: the Day plan's label for the position (or the start chosen today). */
  minutes: number | null;
  status: string;
  confirmation: string;
  kind: VisitKind;
  href: string | null;
}

/* ------------------------------------------------------------------------------------------------
 * Emails armed ahead of time for the day (event reminders, D-1.7.7-0)
 * ---------------------------------------------------------------------------------------------- */

export type HouseEmailState = 'scheduled' | 'sent' | 'cancelled' | 'notSent' | 'failed';

export interface HouseEmail {
  communicationId: string;
  eventId: string;
  eventName: string;
  /** When it leaves, or left (an ISO instant). */
  at: string;
  state: HouseEmailState;
  /** Sent: how many it reached (null while it waits). */
  tenants: number | null;
  volunteers: number | null;
  /** Cancelled: by whom (a first name), or why when it was not a person. */
  cancelledBy: string | null;
  cancelledWhy: 'person' | 'settings-off' | 'event-cancelled' | null;
  /** Its row in the Outbox, where Cancel lives. */
  href: string;
}

/** The Outbox tab a row stands on. */
export function outboxTabOf(state: HouseEmailState): 'queued' | 'sent' | 'cancelled' | 'failed' {
  return state === 'scheduled' ? 'queued' : state === 'sent' ? 'sent' : state === 'cancelled' ? 'cancelled' : 'failed';
}
/** The address of a row in the Outbox (`/messages?tab=…&open=<CommunicationID>`). */
export const outboxHref = (communicationId: string, state: HouseEmailState) => `/messages?tab=${outboxTabOf(state)}&open=${encodeURIComponent(communicationId)}`;

/** The Outbox row's status (SM_Communications CommunicationStatus) as the state Today says in words. */
export function emailStateOf(status: string): HouseEmailState {
  if (status === 'Sent') return 'sent';
  if (status === 'Cancelled' || status === 'Superseded') return 'cancelled';
  if (status === 'Not Required') return 'notSent';
  if (status === 'Pending Approval' || status === 'Failed') return 'failed';
  return 'scheduled';
}

/* ------------------------------------------------------------------------------------------------
 * Recent reports — one line each, never the narrative
 * ---------------------------------------------------------------------------------------------- */

export const HOUSE_REPORT_KINDS = ['closeoutNotes', 'incident', 'unitCondition', 'homeVisit'] as const;
export type HouseReportKind = (typeof HOUSE_REPORT_KINDS)[number];

export interface HouseReport {
  id: string;
  kind: HouseReportKind;
  program: HouseProgram | null;
  dayKey: string;
  /** Not for the `titles` level. */
  unit: string | null;
  tenantName: string | null;
  href: string | null;
  openIn: 'housekeeping' | 'companion' | 'reading' | null;
}

/* ------------------------------------------------------------------------------------------------
 * The answer
 * ---------------------------------------------------------------------------------------------- */

export const HOUSE_SECTIONS = ['attention', 'visits', 'emails', 'reports'] as const;
export type HouseSection = (typeof HOUSE_SECTIONS)[number];

export interface HouseTodayResponse {
  /** The day shown (yyyyMMdd). */
  dayKey: string;
  todayKey: string;
  names: NameLevel;
  /** Needs your attention — always about now (today), whatever day is shown. */
  attention: HouseAttnLine[];
  visits: HouseVisit[];
  emails: HouseEmail[];
  /** The last two days (today and yesterday). */
  reports: HouseReport[];
  /** Parts that could not be read this time — the page says so and draws the rest. */
  unavailable?: HouseSection[];
}

/** Today's roles: the administrator, a co-administrator (full) and the viewer (titles and counts). */
export const HOUSE_TODAY_ROLES = ['admin', 'coadmin', 'viewer'] as const;

/* ------------------------------------------------------------------------------------------------
 * The greeting's name — "Call me" in My settings
 * ---------------------------------------------------------------------------------------------- */

export { CALL_ME_MAX, cleanCallMe } from './my-settings';

/** The name the greeting uses: the person's "Call me", or — not chosen — their first name. */
export function greetingName(callMe: string | null | undefined, name: string | null | undefined): string {
  const own = typeof callMe === 'string' ? cleanCallMe(callMe) : null;
  if (own) return own;
  return (name ?? '').trim().split(/\s+/)[0] ?? '';
}
